import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  NgModule,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { TemplateDirective } from '@app/shared/ui-components/organisms';
import { SystemIconComponentModule } from '@app/shared/ui-components/atoms/icon/icon.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { AccordionComponent } from '@app/shared/ui-components';

@Component({
  selector: 'app-system-accordion-tab',
  templateUrl: './accordion-tab.component.html',
  styleUrl: './accordion-tab.component.scss',
  animations: [
    trigger('tabContent', [
      state(
        'hidden',
        style({
          height: '0',
          visibility: 'hidden',
        })
      ),
      state(
        'visible',
        style({
          height: '*',
          visibility: 'visible',
        })
      ),
      transition('visible <=> hidden', [animate('{{transitionParams}}')]),
      transition('void => *', animate(0)),
    ]),
  ],
})
export class AccordionTabComponent implements AfterContentInit, OnDestroy {
  /**
   * Inline style of the tab header.
   * @group Props
   */
  @Input() headerStyle: { [klass: string]: any } | null | undefined;
  @Input() headerClasses = '';
  /**
   * Inline style of the tab.
   * @group Props
   */
  @Input() tabStyle: { [klass: string]: any } | null | undefined;
  /**
   * Inline style of the tab content.
   * @group Props
   */
  @Input() contentStyle: { [klass: string]: any } | null | undefined;
  /**
   * Style class of the tab.
   * @group Props
   */
  @Input() tabStyleClass: string | undefined;
  /**
   * Whether the tab is disabled.
   * @group Props
   */
  @Input() disabled: boolean | undefined;
  /**
   * Whether a lazy loaded panel should avoid getting loaded again on reselection.
   * @group Props
   */
  @Input() cache = true;
  /**
   * Transition options of the animation.
   * @group Props
   */
  @Input() transitionOptions = '400ms cubic-bezier(0.86, 0, 0.07, 1)';
  /**
   * The value that returns the selection.
   * @group Props
   */
  @Input() get selected(): boolean {
    return this._selected;
  }
  set selected(val: boolean) {
    this._selected = val;

    if (!this.loaded) {
      if (this._selected && this.cache) {
        this.loaded = true;
      }

      this.changeDetector.detectChanges();
    }
  }
  /**
   * The aria-level that each accordion header will have. The default value is 2 as per W3C specifications
   * @group Props
   */
  @Input() headerAriaLevel = 2;
  /**
   * Event triggered by changing the choice.
   * @param {boolean} value - Boolean value indicates that the option is changed.
   * @group Emits
   */
  @Output() selectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ContentChildren(TemplateDirective) templates!: QueryList<TemplateDirective>;

  bodyTemplate!: TemplateRef<any>;

  headerTemplate!: TemplateRef<any>;

  iconTemplate!: TemplateRef<any>;

  loaded = false;

  private _selected = false;

  constructor(
    public changeDetector: ChangeDetectorRef,
    public accordion: AccordionComponent
  ) {}

  ngAfterContentInit() {
    this.templates.forEach(template => {
      switch (template.name) {
        case 'header':
          this.headerTemplate = template.template;
          break;

        case 'body':
          this.bodyTemplate = template.template;
          break;
      }
    });
  }

  ngOnDestroy() {
    this.accordion.tabs.splice(this.findTabIndex(), 1);
  }

  toggle(event?: MouseEvent | KeyboardEvent) {
    if (this.disabled) {
      return false;
    }

    const index = this.findTabIndex();

    if (this.selected) {
      this.selected = false;
      this.accordion.onClose.emit({
        originalEvent: event!,
        index: index,
      });
    } else {
      if (!this.accordion.multiple) {
        for (let i = 0; i < this.accordion.tabs.length; i++) {
          if (this.accordion.tabs[i].selected) {
            this.accordion.tabs[i].selected = false;
            this.accordion.tabs[i].selectedChange.emit(false);
            this.accordion.tabs[i].changeDetector.markForCheck();
          }
        }
      }

      this.selected = true;
      this.loaded = true;
      this.accordion.onOpen.emit({
        originalEvent: event!,
        index: index,
      });
    }

    this.selectedChange.emit(this.selected);
    this.accordion.updateActiveIndex();
    this.changeDetector.markForCheck();

    event?.preventDefault();

    return true;
  }

  findTabIndex() {
    let index = -1;
    for (let i = 0; i < this.accordion.tabs.length; i++) {
      if (this.accordion.tabs[i] == this) {
        index = i;
        break;
      }
    }

    return index;
  }
}

@NgModule({
  declarations: [AccordionTabComponent],
  exports: [AccordionTabComponent, TemplateDirective],
  imports: [CommonModule, TemplateDirective, SystemIconComponentModule],
})
export class SystemAccordionTabModule {}
